import { useState } from "react";
import { HiArrowLeft } from "react-icons/hi2";
import { TfiClose } from "react-icons/tfi";
import { Sheet } from "react-modal-sheet";

import { openLink, useLaunchParams } from "@tma.js/sdk-react";

import { ZkCertType } from "shared/config/consts/zk-cert-types";
import { HolderCommitment } from "shared/tg/utils/generate-holder-commitment";
import { ProgressBar } from "shared/ui/progress-bar";
import { cn } from "shared/utils/cn";

import { SelectCountry } from "./select-country";
import { SelectProvider } from "./select-provider";
import { SelectType } from "./select-type";

const steps = ["select-type", "select-country", "select-provider"] as const;

export type Step = (typeof steps)[number];

type Props = {
  isOpen: boolean;
  hideProgressBar?: boolean;
  holderCommitment: HolderCommitment;
  initialType?: ZkCertType;
  initialStep?: Step;
  initialCountryCode?: string;
  onTypeSelect?: (type: ZkCertType) => void;
  onClose: () => void;
};

export const GetCertificateSheet = (props: Props) => {
  const {
    isOpen,
    initialStep = "select-type",
    initialCountryCode,
    initialType,
    holderCommitment,
    hideProgressBar,
    onClose,
    onTypeSelect,
  } = props;
  const { tgWebAppPlatform } = useLaunchParams();
  const [step, setStep] = useState<Step>(initialStep);

  const [type, setType] = useState<ZkCertType | undefined>(initialType);
  const [_countryCode, setCountryCode] = useState<string | undefined>(
    initialCountryCode
  );

  const showProgressBar = type === "gip1" && !hideProgressBar;

  return (
    <Sheet detent="content" isOpen={isOpen} onClose={onClose}>
      <Sheet.Container>
        <Sheet.Header className="m-auto mt-5 flex w-full max-w-md items-center gap-x-12 px-6">
          {!hideProgressBar && (
            <HiArrowLeft
              className="size-12 cursor-pointer p-2 brightness-125"
              onClick={() => {
                if (type === "gip3") {
                  setStep("select-type");
                  return;
                }
                const activeIndex = steps.findIndex((s) => s === step);
                const prevIndex = Math.max(0, activeIndex - 1);
                const prevStep = steps[prevIndex];
                setStep(prevStep);
              }}
            />
          )}

          {showProgressBar && (
            <ProgressBar steps={steps} activeStep={step} onChange={setStep} />
          )}
          <TfiClose
            onClick={onClose}
            className="ml-auto size-12 cursor-pointer p-2 brightness-125"
          />
        </Sheet.Header>
        <Sheet.Content className="px-6">
          <div
            className={cn(
              "m-auto flex h-full w-full max-w-md flex-col items-center overflow-hidden",
              "max-h-[calc(var(--tg-viewport-stable-height)-150px)]",
              "min-h-[calc(var(--tg-viewport-stable-height)-150px)]"
            )}
          >
            {step === "select-type" && (
              <SelectType
                onSelect={(type) => {
                  onTypeSelect?.(type);
                  if (type === "gip2") return;
                  setType(type);
                  setStep(
                    type === "gip1" ? "select-country" : "select-provider"
                  );
                }}
              />
            )}

            {step === "select-country" && (
              <SelectCountry
                onSelect={(code) => {
                  setCountryCode(code);
                  setStep("select-provider");
                }}
              />
            )}

            {step === "select-provider" && type && (
              <SelectProvider
                type={type}
                onSelect={() => {
                  const searchParams = new URLSearchParams({
                    ...holderCommitment,
                    from: "tg",
                  });

                  if (type === "gip1") {
                    try {
                      openLink.ifAvailable(
                        `https://tmakyc.lookhere.tech/?${searchParams.toString()}`,
                        {
                          tryBrowser:
                            tgWebAppPlatform === "android"
                              ? "chrome"
                              : undefined,
                          tryInstantView: false,
                        }
                      );
                    } catch (error) {
                      console.error(error);
                    }
                  }

                  if (type === "gip3") {
                    try {
                      openLink(
                        import.meta.env.VITE_CHAIN_ID === "843843"
                          ? `https://guardian-cassiopeia.occam.fi/provider/x/?${searchParams.toString()}`
                          : `https://guardian.occam.fi/provider/x/?${searchParams.toString()}`,
                        {
                          tryInstantView: false,
                          tryBrowser:
                            tgWebAppPlatform === "android"
                              ? "chrome"
                              : undefined,
                        }
                      );
                    } catch (error) {
                      console.error(error);
                    }
                  }
                }}
              />
            )}
          </div>
        </Sheet.Content>
      </Sheet.Container>
      <Sheet.Backdrop />
    </Sheet>
  );
};
